import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Download, AlertTriangle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Site { id: string; code: string | null; name_en: string | null }
interface Vehicle { id: string; plateEn: string | null; code: string | null }
interface Driver  { id: string; fullNameEn: string | null; code: string | null }

const METRICS = [
  { v: 'trips',         l: 'Trips' },
  { v: 'delivered',     l: 'Delivered qty' },
  { v: 'revenue',       l: 'Revenue' },
  { v: 'distance',      l: 'Distance (km)' },
  { v: 'fuel_sar',      l: 'Fuel SAR' },
  { v: 'fuel_liters',   l: 'Fuel liters' },
  { v: 'idle_waste',    l: 'Idle waste cost' },
  { v: 'expenses',      l: 'Expenses' },
  { v: 'maintenance',   l: 'Maintenance' },
  { v: 'cash_variance', l: 'Cash variance' },
] as const;

const GROUPS = [
  { v: 'none', l: 'No grouping (totals)' },
  { v: 'site', l: 'Site' },
  { v: 'vehicle', l: 'Vehicle' },
  { v: 'driver', l: 'Driver' },
  { v: 'day', l: 'Day' },
  { v: 'week', l: 'Week' },
  { v: 'month', l: 'Month' },
] as const;

interface Row { group: string; [k: string]: string | number }

export default function CustomReportPage() {
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [vehicleIds, setVehicleIds] = useState<string[]>([]);
  const [driverIds, setDriverIds] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<string[]>(['trips', 'revenue', 'fuel_sar']);
  const [groupBy, setGroupBy] = useState<string>('none');

  const [sites, setSites] = useState<Site[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get<Site[]>('/api/v1/daily-reports/sites').then((r) => setSites(r.data)).catch(() => null);
    apiClient.get('/api/v1/vehicles?status=active&pageSize=200').then((r) => {
      const d = r.data as unknown;
      if (Array.isArray(d)) setVehicles(d as Vehicle[]);
      else if (d && typeof d === 'object' && 'items' in d) setVehicles(((d as { items?: Vehicle[] }).items) ?? []);
    }).catch(() => null);
    apiClient.get('/api/v1/drivers?status=active&pageSize=200').then((r) => {
      const d = r.data as unknown;
      if (Array.isArray(d)) setDrivers(d as Driver[]);
      else if (d && typeof d === 'object' && 'items' in d) setDrivers(((d as { items?: Driver[] }).items) ?? []);
    }).catch(() => null);
  }, []);

  function toggleArr(arr: string[], v: string, set: (a: string[]) => void) {
    if (arr.includes(v)) set(arr.filter((x) => x !== v));
    else set([...arr, v]);
  }

  async function run(format: 'json' | 'pdf' | 'xlsx') {
    setRunning(true); setError(null);
    try {
      if (format === 'json') {
        const r = await apiClient.post<{ rows: Row[] }>('/api/v1/reports/custom', {
          from, to,
          siteIds: siteIds.length > 0 ? siteIds : undefined,
          vehicleIds: vehicleIds.length > 0 ? vehicleIds : undefined,
          driverIds: driverIds.length > 0 ? driverIds : undefined,
          metrics, groupBy, format,
        });
        setRows(r.data.rows);
      } else {
        const jwt = getStoredToken();
        const res = await fetch(API_BASE + '/api/v1/reports/custom', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: 'Bearer ' + jwt } : {}) },
          body: JSON.stringify({ from, to, siteIds: siteIds.length > 0 ? siteIds : undefined, vehicleIds: vehicleIds.length > 0 ? vehicleIds : undefined, driverIds: driverIds.length > 0 ? driverIds : undefined, metrics, groupBy, format }),
        });
        if (!res.ok) { setError('Export failed: ' + res.statusText); return; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'custom_' + from + '_to_' + to + '.' + format;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  const cols = rows.length > 0 ? Object.keys(rows[0]) : ['group', ...metrics];

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard/reports"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Reports</Button></Link>
          <div className="text-sm font-medium">Custom report builder</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-border p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1"><Label htmlFor="from">From</Label><Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="space-y-1"><Label htmlFor="to">To</Label><Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          </div>

          <MultiSelect label="Sites" options={sites.map((s) => ({ id: s.id, label: s.name_en ?? s.code ?? s.id.slice(0, 8) }))} selected={siteIds} onToggle={(v) => toggleArr(siteIds, v, setSiteIds)} />
          <MultiSelect label="Vehicles" options={vehicles.map((v) => ({ id: v.id, label: v.plateEn ?? v.code ?? v.id.slice(0, 8) }))} selected={vehicleIds} onToggle={(v) => toggleArr(vehicleIds, v, setVehicleIds)} />
          <MultiSelect label="Drivers" options={drivers.map((d) => ({ id: d.id, label: d.fullNameEn ?? d.code ?? d.id.slice(0, 8) }))} selected={driverIds} onToggle={(v) => toggleArr(driverIds, v, setDriverIds)} />

          <div>
            <Label>Metrics</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {METRICS.map((m) => (
                <label key={m.v} className="inline-flex items-center gap-1.5 cursor-pointer text-sm rounded-md border border-border px-2.5 py-1">
                  <input type="checkbox" checked={metrics.includes(m.v)} onChange={() => toggleArr(metrics, m.v, setMetrics)} />
                  {m.l}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="gb">Group by</Label>
            <select id="gb" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base mt-1">
              {GROUPS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
            </select>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={() => run('json')} disabled={running || metrics.length === 0} className="gap-2">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Run</Button>
            <Button variant="outline" onClick={() => run('pdf')} disabled={running} className="gap-2"><Download className="h-4 w-4" /> PDF</Button>
            <Button variant="outline" onClick={() => run('xlsx')} disabled={running} className="gap-2"><Download className="h-4 w-4" /> Excel</Button>
          </div>
        </div>

        {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>}

        {rows.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                  <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2">{c}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                      {cols.map((c) => <td key={c} className="px-3 py-2 font-mono text-xs">{typeof r[c] === 'number' ? (r[c] as number).toLocaleString('en-US') : String(r[c] ?? '—')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function MultiSelect({ label, options, selected, onToggle }: { label: string; options: { id: string; label: string }[]; selected: string[]; onToggle: (id: string) => void }) {
  if (options.length === 0) return null;
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2 mt-1.5 max-h-32 overflow-y-auto">
        {options.map((o) => (
          <label key={o.id} className={'inline-flex items-center gap-1.5 cursor-pointer text-sm rounded-md border px-2.5 py-1 ' + (selected.includes(o.id) ? 'border-foreground bg-foreground text-background' : 'border-border')}>
            <input type="checkbox" className="hidden" checked={selected.includes(o.id)} onChange={() => onToggle(o.id)} />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}
